import { useNavigate, useParams } from "@tanstack/react-router"
import { useCallback } from "react"
import { useSessionStorage } from "usehooks-ts"

import { presetStorageKey } from "~/components/planning/presetView"
import { demandPresetGroupId } from "~/components/demand-2/shared/useOpenInPreorders"
import { useTableSearch } from "~/hooks/useTableSearch"
import { useRouteFilterScope } from "~/providers/FilterScopeProvider"
import { ownInsightAnswerFilter } from "~/components/demand-2/shared/ownInsightAnswers"
import { demandTab } from "~/components/demand-2/demandRoutes"
import type { FramingQuestionId } from "~/utils/demandFraming"

const TOTAL_TAB = demandTab("fashion", "demand")

/** Must match the grid id `TotalTable` passes. */
export const totalTableId = (collectionId: number) => `demand2-total-${collectionId}`

/**
 * Opens a fashion collection's Demand > Total view, closing the Demand configuration dialog; the route
 * keeps the current search (group `isGroup` included) minus `strategy`. `showOwnAnswers` replaces
 * the page filters with one own-answer rule and clears the table search, so nothing left behind
 * hides the rows.
 */
export const useOpenInTotal = () => {
  const navigate = useNavigate()
  // The route's own id (the group's on a group route): what the tab keys its view and table on.
  const routeId = (useParams({ strict: false }) as { collectionId?: string }).collectionId ?? ""
  const tableId = totalTableId(Number(routeId))
  const [, setPresetId] = useSessionStorage<string | null>(
    presetStorageKey(demandPresetGroupId(Number(routeId))),
    null,
  )
  const { setFilters } = useRouteFilterScope(TOTAL_TAB.routeId)
  const { setSearchText } = useTableSearch(tableId)

  const go = useCallback(() => {
    setPresetId("total")
    void navigate({
      to: TOTAL_TAB.to,
      params: { collectionId: routeId },
      search: (prev: Record<string, unknown>) => ({ ...prev, strategy: undefined }),
    })
  }, [setPresetId, navigate, routeId])

  const showOwnAnswers = useCallback(
    (question: FramingQuestionId) => {
      setSearchText("")
      void setFilters([ownInsightAnswerFilter(question)])
      go()
    },
    [setSearchText, setFilters, go],
  )

  return { showOwnAnswers }
}
